import {
  MessageSquare,
  Menu,
  CalendarDays,
  DoorOpen,
  CircleHelp,
  BarChart3,
  Disc3,
  Image,
  FileText,
  Contact,
  ListMusic,
  Megaphone,
  Wallet,
  Settings,
  Bus,
  type LucideIcon,
} from "lucide-react";
import { AntennaGlyph, DosGlyph, HomeGlyph, SocialsGlyph } from "./mobileGlyphs.js";

/**
 * Presentational mobile-nav registry — a tab/More key → { route, icon }. The SET of keys is SERVER
 * data (`nav.mobile`, the "navigation as data" contract); this map is pure chrome — the SPA route + the
 * lucide glyph — and is role-INDEPENDENT, so the client still holds NO role→tab matrix. It is the mobile
 * mirror of ArtistShell's grouped-IA layout (`buildNavItems`). A key with no `route` (e.g. `more`, or the
 * `finances` [future] teaser) is handled by the shell, never navigated to.
 */
interface MobileNavMeta {
  route?: string;
  icon: LucideIcon;
  /** The More-sheet subtitle line (CD `MORE_ITEMS.sub` copy) — design copy, role-independent chrome. */
  sub?: string;
}

const NAV_META: Record<string, MobileNavMeta> = {
  // Bottom-bar tabs
  // Home, Socials, DOS and Antenna are the legacy glyphs (ART-185, PO 2026-10-02) — see mobileGlyphs.tsx.
  home: { route: "/", icon: HomeGlyph },
  content: { route: "/socials", icon: SocialsGlyph, sub: "Posts · UGC · top creators" },
  dos: { route: "/dos", icon: DosGlyph },
  inbox: { route: "/messages", icon: MessageSquare },
  more: { icon: Menu }, // opens the More sheet — no route
  schedule: { route: "/calendar", icon: CalendarDays, sub: "Shows · holds · tour routing" },
  // Both open the read-only mobile shows list (`/shows`), NOT the desktop `/booking` pipeline they used to
  // dump every mobile seat on (ART-153 pass two, spec §3 "mobile books nothing"). The TM's Guest List tab
  // lands there too: a confirmed show's row opens its door — "the door opens per-show from the shows list".
  // A DOOR, not a clipboard (ART-185): the guest list is tonight's door list, and a generic list glyph said
  // nothing a "Contacts"-style person icon didn't.
  guest_list: { route: "/shows", icon: DoorOpen },
  shows: { route: "/shows", icon: CalendarDays },
  music: { route: "/music", icon: BarChart3, sub: "Playlists · radio · monthly listeners" },
  releases: { route: "/releases", icon: Disc3, sub: "Catalog · per-release stats" },
  // More-sheet destinations
  assets: { route: "/assets", icon: Image, sub: "Press kit · photos · bio" },
  // ARTIST CNTRL PRO (ART-146). The ROW only reaches the sheet on a Pro band holding production:read — the
  // server decides that, as it does every key here; this map stays pure chrome and role-independent.
  advance: { route: "/advance", icon: FileText, sub: "Departments · venue links" },
  // PRO (ART-148) — the road editor; the server sends this key only to owner/AV + TM on a Pro band.
  buses: { route: "/buses", icon: Bus, sub: "Bunks · drivers · codes" },
  contacts: { route: "/contacts", icon: Contact, sub: "Venues, bookers, members, press" },
  marketing: { route: "/marketing", icon: Megaphone, sub: "Boosts · campaigns · press" },
  antenna: { route: "/antenna", icon: AntennaGlyph, sub: "Fan club · announcements · backstage" },
  rehearsal: { icon: ListMusic, sub: "Setlist · Practice files · Lyrics" }, // [future] teaser, no route
  finances: { icon: Wallet, sub: "Settle · expenses · payouts" }, // [future] — the Q3-2026 teaser, no route
  settings: { route: "/settings", icon: Settings, sub: "Account · notifications · billing" },
};

/** The SPA route for a nav key, or undefined for the sheet trigger / a coming-soon teaser. */
export function mobileNavRoute(key: string): string | undefined {
  return NAV_META[key]?.route;
}

/**
 * The lucide glyph for a nav key. An UNKNOWN key is loud, not silent (ART-185): it draws a "?" placeholder —
 * never the More menu glyph, which read as a second More button — and warns in dev. The keys are SERVER data,
 * so a key the server adds before this map learns it is exactly the case this exists for; legacy shipped its
 * Rehearsal row as an empty circle for want of it.
 */
export function mobileNavIcon(key: string): LucideIcon {
  const icon = NAV_META[key]?.icon;
  if (icon) return icon;
  if (import.meta.env.DEV) console.warn(`[mobile nav] no icon for nav key "${key}" — add it to NAV_META`);
  return CircleHelp;
}

/** The More-sheet subtitle for a nav key (undefined = no sub line). */
export function mobileNavSub(key: string): string | undefined {
  return NAV_META[key]?.sub;
}

/** True iff `route` is the active surface for the given tab key (prefix match, `/` matches only itself). */
export function isActiveTab(key: string, pathname: string): boolean {
  const route = NAV_META[key]?.route;
  if (!route) return false;
  return route === "/" ? pathname === "/" : pathname === route || pathname.startsWith(`${route}/`);
}

/**
 * Routes that take the WHOLE screen — no header, no tab bar (ART-159, C-Mobile).
 *
 * An open conversation is the one surface in the mobile app that owns its own chrome: it draws a sticky
 * header with the chat's name, its tags and a back arrow, and the app's header above it would put two back
 * affordances and two titles on one screen. The tab bar goes for the same reason the keyboard already hid
 * it — the composer is the bottom of the screen, and a bar under it is the thing a thumb hits instead.
 *
 * **A PREDICATE over the pathname, not a flag the screen sets.** The shell decides its own chrome, and a
 * routed screen reaching up to switch the header off is a child owning its parent's layout — the sort of
 * coupling that survives until two screens want it and disagree. The LIST is not a takeover: it is an
 * ordinary tab destination with a tab bar under it, which is why this matches only a thread.
 */
export function isFullScreenRoute(pathname: string): boolean {
  // `/messages/<id>` and its roster twin — the LIST (`/messages`) is deliberately not one.
  return /^\/(roster\/)?messages\/[^/]+$/.test(pathname);
}

/** The DOS tab (`/dos`, list + detail) draws its OWN chrome and kills the shell's top bar (Ryan, ART-174).
 *  Unlike a full-screen route it KEEPS the bottom tab bar — DOS is the centre tab. The per-show sub-screens
 *  (`/shows/:id/…`) are NOT this: they keep the shell header + its navbar back. */
export function isDosTabRoute(pathname: string): boolean {
  return pathname === "/dos";
}
